/* eslint-disable no-unsafe-optional-chaining */
import {
  selectEndToEndTest,
  selectGAFeatures,
  selectInstitution,
  selectUser,
} from "../../entities";
import { createDeepEqualSelector } from "../../entities/utils/createDeepEqualSelector";
import { featureEquals } from "../../../../Feature";

import { isFeatureEnabled } from "./isEnabled";
import { shouldIncludeMenuItem } from "./utils";

import type { RootState } from "../../entities/store";
import type { FeatureQuery, FeatureValue } from "./types";
import type { RenderItemMenuProps } from "../../../../types";

export const selectFeatures = createDeepEqualSelector(
  [selectGAFeatures, selectInstitution, selectUser, selectEndToEndTest],
  (gaFeatures, institution, user, { featureOverrides }) => {
    // If we have user features (which we get right after the user authenticates,
    // Use them instead of the public institution features received before auth
    return {
      ...gaFeatures,
      ...(user?.features || institution?.features),
      ...featureOverrides,
    } as API.Features;
  },
);

export function selectFeatureEnabled(
  state: RootState,
  or?: FeatureQuery["or"],
  and?: FeatureQuery["and"],
  not?: FeatureQuery["not"],
) {
  const flags = selectFeatures(state);
  return isFeatureEnabled(flags, { or, and, not });
}

export function selectFeatureEquals(
  state: RootState,
  flag: string,
  setting?: FeatureValue | FeatureValue[],
) {
  const flags = selectFeatures(state);
  return featureEquals(flags, flag, setting);
}

export const selectTopMenu = createDeepEqualSelector(
  [selectFeatures, (state) => state],
  (features) => {
    const topMenu = features.top_menu;

    if (topMenu) {
      return Object.entries(topMenu)
        .map(([name, itemProps]) => {
          return { name, itemProps };
        })
        .filter(({ itemProps }) => {
          if (itemProps?.feature_flag) {
            return isFeatureEnabled(features, { or: itemProps.feature_flag });
          }
          return true;
        });
    }

    return [];
  },
);

type NestedMenuWithName = {
  nestedMenu?: RenderItemMenuProps<API.AnyMenuItem>[];
  menuName?: string;
};

export const selectNestedMenuWithName = createDeepEqualSelector(
  [selectFeatures, selectTopMenu],
  (features, topMenu): NestedMenuWithName => {
    // There should only be one nested menu in this setting
    const nestedMenuItem = topMenu.find((item) => {
      return item.itemProps?.menu;
    });

    let extractedMenu: Record<string, API.AnyMenuItem>;
    let result;

    if (nestedMenuItem?.itemProps && "menu" in nestedMenuItem?.itemProps) {
      extractedMenu = nestedMenuItem.itemProps.menu;

      if (extractedMenu) {
        const formatted = Object.entries(extractedMenu).map(
          ([name, itemProps]) => {
            return { name, itemProps };
          },
        );

        result = formatted.filter(({ itemProps }) => {
          if ("feature_flag" in itemProps && itemProps.feature_flag) {
            return isFeatureEnabled(features, { or: itemProps.feature_flag });
          }
          return true;
        });
      }
    }
    return {
      nestedMenu: result,
      menuName: nestedMenuItem?.name,
    };
  },
);

export const selectSidebarMenu = createDeepEqualSelector(
  [selectFeatures, (state) => state],
  (features) => {
    const sidebarMenu = features.sidebar_menu as
      | API.SidebarMenuType
      | undefined;

    if (!sidebarMenu) {
      return [];
    }

    const transformMenuOption = (
      [title, { url, icon, new_tab, sort_order }]: [
        string,
        API.SidebarMenuOption,
      ],
      index: number,
    ): API.SidebarMenuSubItem => ({
      title,
      icon,
      route: url || "",
      newTab: new_tab || false,
      // The backend sorts options and fills in sort_order; fall back to position.
      sortOrder: sort_order ?? index,
    });

    const transformMenuItem = ([
      title,
      {
        key,
        icon,
        url,
        naf_apps,
        naf_client_id,
        aoiob,
        naf_sort_order,
        aoiob_sort_order,
        ...props
      },
    ]: [string, API.SidebarMenuItem]) => {
      const menuItem: API.SidebarMenuItem_Resolved = {
        title,
        key,
        icon,
        route: url,
        showNAFApps: naf_apps || false,
        showAOIOB: aoiob || false,
        nafSortOrder: naf_sort_order,
        aoiobSortOrder: aoiob_sort_order,
        nafClientId: naf_client_id,
        subItems: [],
      };

      if (props.options) {
        const optionEntries = Object.entries(props.options);
        menuItem.subItems = optionEntries
          .map(transformMenuOption)
          .filter((_, index) =>
            shouldIncludeMenuItem(features, optionEntries[index][1]),
          );
      }

      /**
       * Checks if the menuOptions list is empty. If empty remove this item,
       * unless the section can still be populated downstream by NAF apps or
       * AOIOB (those live in different selectors / app props and are attached
       * later in the azul layer). Removing such sections here would hide e.g.
       * the Tools menu even when the user has NAF apps to access.
       */
      return props.options &&
        menuItem.subItems.length === 0 &&
        !naf_apps &&
        !aoiob
        ? undefined
        : menuItem;
    };

    const sidebarItems: API.SidebarMenuItem_Resolved[] = [];

    Object.entries(sidebarMenu).forEach(([title, props]) => {
      if (shouldIncludeMenuItem(features, props)) {
        const menuItem = transformMenuItem([title, props]);
        if (menuItem) {
          sidebarItems.push(menuItem);
        }
      }
    });

    return sidebarItems;
  },
);
